import React, { useMemo } from 'react';
import { Box, Button, Chip, LinearProgress, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { IServices, ISaleOrderListItem, SaleOrderInvoicingStatusEnum, SegmentEnum } from 'features/salesOrder/types';
import SaleOrderInvoicingStatusCell from 'features/salesOrder/components/SaleOrderInvoicingStatusCell';
import { computeOrderBillingSummary } from '../../billingEngine';
import { formatAmount, todayIso } from '../../uiHelpers';
import { useSaleOrderInvoicing } from './InvoicingContext';

/**
 * Indicadores de facturación para el home de la orden: semáforo (mismo
 * criterio que el listado), % facturado y anticipos sin facturar.
 * Los datos vienen del SaleOrderInvoicingProvider (lazy, no bloquea la carga).
 */

const TOLERANCE = 0.01;

/** El motor de facturación razona en moneda principal, no en la del servicio. */
const MAIN_CURRENCY = 'USD';

interface InvoicingSummaryBoxProps {
	services: IServices[];
	segmentType?: SegmentEnum;
	travelReturnDate?: string;
	onGoToInvoices: () => void;
}

export const InvoicingSummaryBox = ({
	services,
	segmentType,
	travelReturnDate,
	onGoToInvoices,
}: InvoicingSummaryBoxProps): JSX.Element => {
	const { invoices, invoicesLoaded, invoicesError, freeReceipts, receiptsLoaded } = useSaleOrderInvoicing();

	const summary = useMemo(() => {
		const { total, billed, pending, pct } = computeOrderBillingSummary(services, invoices);

		let status: SaleOrderInvoicingStatusEnum | undefined;
		if (total > 0) {
			if (billed <= TOLERANCE) status = SaleOrderInvoicingStatusEnum.Pending;
			else if (billed >= total - TOLERANCE) status = SaleOrderInvoicingStatusEnum.Total;
			else status = SaleOrderInvoicingStatusEnum.Partial;
		}

		return { total, billed, pending, pct, status };
	}, [services, invoices]);

	const isReturnDateReached = Boolean(travelReturnDate && travelReturnDate.slice(0, 10) <= todayIso());

	/**
	 * SaleOrderInvoicingStatusCell lee solo estos campos del row del listado;
	 * acá se sintetizan a partir de las facturas ya cargadas de la orden.
	 */
	const statusRow = useMemo(
		(): ISaleOrderListItem =>
			({
				invoicedAmount: summary.billed,
				saleAmount: summary.total || undefined,
				pendingInvoicingAmount: summary.total > 0 ? summary.pending : undefined,
				invoicingStatus: summary.status,
				segmentType,
				isReturnDateReached,
			}) as unknown as ISaleOrderListItem,
		[summary, segmentType, isReturnDateReached],
	);

	const freeReceiptsAmountUsd = useMemo(
		() => freeReceipts.reduce((acc, r) => acc + (r.amountInMainCurrency ?? 0), 0),
		[freeReceipts],
	);

	return (
		<Box
			sx={{
				border: '1px solid #e0e0e0',
				borderRadius: 2,
				backgroundColor: '#fafafa',
				p: 1.5,
				px: 2,
				mb: 2,
			}}
		>
			<Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
				<Stack direction="row" spacing={1} alignItems="center">
					<ReceiptLongIcon fontSize="small" color="action" />
					<Typography variant="subtitle2">Facturación</Typography>
				</Stack>

				{!invoicesLoaded ? (
					<Skeleton variant="text" width={220} />
				) : invoicesError ? (
					<Typography variant="caption" color="error">
						{invoicesError}
					</Typography>
				) : (
					<>
						<SaleOrderInvoicingStatusCell row={statusRow} />

						{summary.total > 0 && (
							<Tooltip
								title={`Facturado ${formatAmount(summary.billed, MAIN_CURRENCY)} de ${formatAmount(
									summary.total,
									MAIN_CURRENCY,
								)} · Falta ${formatAmount(summary.pending, MAIN_CURRENCY)}`}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
									<LinearProgress
										variant="determinate"
										value={Math.min(100, summary.pct)}
										color={summary.pct >= 100 ? 'success' : 'primary'}
										sx={{ height: 6, borderRadius: 3, flex: 1 }}
									/>
									<Typography variant="caption" color="text.secondary">
										{summary.pct}%
									</Typography>
								</Box>
							</Tooltip>
						)}

						{receiptsLoaded && freeReceipts.length > 0 && (
							<Tooltip title="Anticipos cobrados que todavía no están vinculados a ninguna factura">
								<Chip
									size="small"
									color="warning"
									label={`${freeReceipts.length} anticipo${
										freeReceipts.length === 1 ? '' : 's'
									} sin facturar · ${formatAmount(Number(freeReceiptsAmountUsd.toFixed(2)), 'USD')}`}
								/>
							</Tooltip>
						)}
					</>
				)}

				<Box sx={{ flex: 1 }} />

				<Button size="small" endIcon={<ArrowForwardIcon />} onClick={onGoToInvoices}>
					Ir a Facturación
				</Button>
			</Stack>
		</Box>
	);
};

export default InvoicingSummaryBox;
